/**
 * Астрономическое ядро натальной карты.
 *
 * Считаем по-настоящему, а не рисуем правдоподобные числа:
 *  · Солнце — формулы Мееуса (точность ~0,01°);
 *  · Луна — усечённый ряд Мееуса, гл. 47 (~0,3°);
 *  · планеты — кеплеровы элементы JPL «Approximate Positions of the Major
 *    Planets», пригодны для 1800–2050 (ошибка до ~0,5° у Юпитера и Сатурна);
 *  · Асцендент и MC — из местного звёздного времени и широты.
 *
 * Для определения знака этой точности достаточно везде, кроме границ знаков:
 * см. `nearCusp` — такие положения помечаются, а не выдаются за точные.
 *
 * Чего здесь нет и почему:
 *  · Плутон — мгновенные кеплеровы элементы описывают его плохо, нужна
 *    полноценная эфемерида;
 *  · система домов только цельнознаковая (whole sign) — она не требует
 *    ничего сверх Асцендента и является старейшей традиционной системой.
 */

export type { Lang } from './cards.ts'
import type { Lang } from './cards.ts'
import { loc } from './cards.ts'

const DEG = Math.PI / 180
const rad = (d: number) => d * DEG
const deg = (r: number) => r / DEG
const norm360 = (d: number) => ((d % 360) + 360) % 360

// ── Время ────────────────────────────────────────────────────────────

/** Юлианская дата по григорианскому календарю (Мееус, гл. 7). */
export function julianDay(utc: Date): number {
  let year = utc.getUTCFullYear()
  let month = utc.getUTCMonth() + 1
  const day =
    utc.getUTCDate() +
    (utc.getUTCHours() + utc.getUTCMinutes() / 60 + utc.getUTCSeconds() / 3600) / 24

  if (month <= 2) {
    year -= 1
    month += 12
  }
  const a = Math.floor(year / 100)
  const b = 2 - a + Math.floor(a / 4)
  return (
    Math.floor(365.25 * (year + 4716)) + Math.floor(30.6001 * (month + 1)) + day + b - 1524.5
  )
}

/** Юлианские столетия от эпохи J2000.0. */
const centuries = (jd: number) => (jd - 2451545.0) / 36525

/**
 * Смещение часового пояса в минутах для конкретного момента.
 * Берём из ICU, поэтому исторические переводы часов (включая советское
 * декретное время) учитываются автоматически.
 */
export function timezoneOffsetMinutes(timeZone: string, instant: Date): number {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
  const parts: Record<string, string> = {}
  for (const p of dtf.formatToParts(instant)) parts[p.type] = p.value
  const asUTC = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour) % 24,
    Number(parts.minute),
    Number(parts.second),
  )
  return (asUTC - instant.getTime()) / 60000
}

/**
 * Местное время рождения → UTC. Смещение зависит от самого момента, поэтому
 * уточняем его итеративно (двух проходов хватает и на границе перевода часов).
 */
export function localToUtc(
  y: number,
  m: number,
  d: number,
  hour: number,
  minute: number,
  timeZone: string,
): Date {
  let utc = new Date(Date.UTC(y, m - 1, d, hour, minute))
  for (let i = 0; i < 3; i++) {
    const offset = timezoneOffsetMinutes(timeZone, utc)
    utc = new Date(Date.UTC(y, m - 1, d, hour, minute) - offset * 60000)
  }
  return utc
}

// ── Солнце и Луна ────────────────────────────────────────────────────

/** Видимая эклиптическая долгота Солнца на эпоху даты, градусы. */
export function sunLongitude(jd: number): number {
  const t = centuries(jd)
  const l0 = 280.46646 + 36000.76983 * t + 0.0003032 * t * t
  const m = rad(357.52911 + 35999.05029 * t - 0.0001537 * t * t)
  const c =
    (1.914602 - 0.004817 * t - 0.000014 * t * t) * Math.sin(m) +
    (0.019993 - 0.000101 * t) * Math.sin(2 * m) +
    0.000289 * Math.sin(3 * m)
  const omega = rad(125.04 - 1934.136 * t)
  return norm360(l0 + c - 0.00569 - 0.00478 * Math.sin(omega))
}

/** Эклиптическая долгота Луны, градусы (главные члены ряда ELP). */
export function moonLongitude(jd: number): number {
  const t = centuries(jd)
  const lp = 218.3164477 + 481267.88123421 * t - 0.0015786 * t * t
  const d = rad(297.8501921 + 445267.1114034 * t - 0.0018819 * t * t)
  const m = rad(357.5291092 + 35999.0502909 * t - 0.0001536 * t * t)
  const mp = rad(134.9633964 + 477198.8675055 * t + 0.0087414 * t * t)
  const f = rad(93.272095 + 483202.0175233 * t - 0.0036539 * t * t)

  const terms: [number, number][] = [
    [6.288774, mp],
    [1.274027, 2 * d - mp],
    [0.658314, 2 * d],
    [0.213618, 2 * mp],
    [-0.185116, m],
    [-0.114332, 2 * f],
    [0.058793, 2 * d - 2 * mp],
    [0.057066, 2 * d - m - mp],
    [0.053322, 2 * d + mp],
    [0.045758, 2 * d - m],
    [-0.040923, m - mp],
    [-0.03472, d],
    [-0.030383, m + mp],
    [0.015327, 2 * d - 2 * f],
    [-0.012528, mp + 2 * f],
    [0.01098, mp - 2 * f],
    [0.010675, 4 * d - mp],
    [0.010034, 3 * mp],
    [0.008548, 4 * d - 2 * mp],
  ]

  let lon = lp
  for (const [amp, arg] of terms) lon += amp * Math.sin(arg)
  return norm360(lon)
}

// ── Планеты ──────────────────────────────────────────────────────────

interface Elements {
  /** большая полуось (а.е.), эксцентриситет, наклон, средняя долгота,
   *  долгота перигелия, долгота восходящего узла — и их вековые скорости. */
  a: [number, number]
  e: [number, number]
  i: [number, number]
  l: [number, number]
  peri: [number, number]
  node: [number, number]
}

const ELEMENTS: Record<string, Elements> = {
  earth: {
    a: [1.00000261, 0.00000562], e: [0.01671123, -0.00004392], i: [-0.00001531, -0.01294668],
    l: [100.46457166, 35999.37244981], peri: [102.93768193, 0.32327364], node: [0.0, 0.0],
  },
  mercury: {
    a: [0.38709927, 0.00000037], e: [0.20563593, 0.00001906], i: [7.00497902, -0.00594749],
    l: [252.2503235, 149472.67411175], peri: [77.45779628, 0.16047689], node: [48.33076593, -0.12534081],
  },
  venus: {
    a: [0.72333566, 0.0000039], e: [0.00677672, -0.00004107], i: [3.39467605, -0.0007889],
    l: [181.9790995, 58517.81538729], peri: [131.60246718, 0.00268329], node: [76.67984255, -0.27769418],
  },
  mars: {
    a: [1.52371034, 0.00001847], e: [0.0933941, 0.00007882], i: [1.84969142, -0.00813131],
    l: [-4.55343205, 19140.30268499], peri: [-23.94362959, 0.44441088], node: [49.55953891, -0.29257343],
  },
  jupiter: {
    a: [5.202887, -0.00011607], e: [0.04838624, -0.00013253], i: [1.30439695, -0.00183714],
    l: [34.39644051, 3034.74612775], peri: [14.72847983, 0.21252668], node: [100.47390909, 0.20469106],
  },
  saturn: {
    a: [9.53667594, -0.0012506], e: [0.05386179, -0.00050991], i: [2.48599187, 0.00193609],
    l: [49.95424423, 1222.49362201], peri: [92.59887831, -0.41897216], node: [113.66242448, -0.28867794],
  },
  uranus: {
    a: [19.18916464, -0.00196176], e: [0.04725744, -0.00004397], i: [0.77263783, -0.00242939],
    l: [313.23810451, 428.48202785], peri: [170.9542763, 0.40805281], node: [74.01692503, 0.04240589],
  },
  neptune: {
    a: [30.06992276, 0.00026291], e: [0.00859048, 0.00005105], i: [1.77004347, 0.00035372],
    l: [-55.12002969, 218.45945325], peri: [44.96476227, -0.32241464], node: [131.78422574, -0.00508664],
  },
}

/** Гелиоцентрические прямоугольные координаты в эклиптике J2000. */
function heliocentric(body: string, t: number): [number, number, number] {
  const el = ELEMENTS[body]
  const a = el.a[0] + el.a[1] * t
  const e = el.e[0] + el.e[1] * t
  const i = rad(el.i[0] + el.i[1] * t)
  const l = el.l[0] + el.l[1] * t
  const peri = el.peri[0] + el.peri[1] * t
  const node = rad(el.node[0] + el.node[1] * t)
  const argPeri = rad(peri) - node

  // Средняя аномалия приводится к [-180°, 180°], иначе решение Кеплера
  // сходится медленно на больших витках.
  let m = norm360(l - peri)
  if (m > 180) m -= 360
  m = rad(m)

  // Уравнение Кеплера, метод Ньютона.
  let ecc = m + e * Math.sin(m)
  for (let k = 0; k < 12; k++) {
    const dm = m - (ecc - e * Math.sin(ecc))
    const de = dm / (1 - e * Math.cos(ecc))
    ecc += de
    if (Math.abs(de) < 1e-12) break
  }

  const xOrb = a * (Math.cos(ecc) - e)
  const yOrb = a * Math.sqrt(1 - e * e) * Math.sin(ecc)

  const cosW = Math.cos(argPeri)
  const sinW = Math.sin(argPeri)
  const cosO = Math.cos(node)
  const sinO = Math.sin(node)
  const cosI = Math.cos(i)
  const sinI = Math.sin(i)

  return [
    (cosW * cosO - sinW * sinO * cosI) * xOrb + (-sinW * cosO - cosW * sinO * cosI) * yOrb,
    (cosW * sinO + sinW * cosO * cosI) * xOrb + (-sinW * sinO + cosW * cosO * cosI) * yOrb,
    sinW * sinI * xOrb + cosW * sinI * yOrb,
  ]
}

/** Общая прецессия по долготе от J2000 к эпохе даты, градусы. */
const precession = (t: number) => 1.396971 * t + 0.0003086 * t * t

/** Геоцентрическая эклиптическая долгота планеты на эпоху даты, градусы. */
export function planetLongitude(body: string, jd: number): number {
  const t = centuries(jd)
  const [px, py] = heliocentric(body, t)
  const [ex, ey] = heliocentric('earth', t)
  return norm360(deg(Math.atan2(py - ey, px - ex)) + precession(t))
}

// ── Асцендент и MC ───────────────────────────────────────────────────

/** Среднее гринвичское звёздное время, градусы. */
function gmst(jd: number): number {
  const t = centuries(jd)
  return norm360(
    280.46061837 +
      360.98564736629 * (jd - 2451545.0) +
      0.000387933 * t * t -
      (t * t * t) / 38710000,
  )
}

/** Наклон эклиптики, градусы. */
const obliquity = (t: number) => 23.439291 - 0.0130042 * t - 1.64e-7 * t * t

export interface Angles {
  /** Асцендент — градус эклиптики, восходящий на востоке. */
  asc: number
  /** Середина неба. */
  mc: number
}

/** Асцендент и MC для момента и точки на Земле (широта/долгота в градусах). */
export function angles(jd: number, latitude: number, longitudeEast: number): Angles {
  const t = centuries(jd)
  const eps = rad(obliquity(t))
  const ramc = rad(norm360(gmst(jd) + longitudeEast))
  const phi = rad(latitude)

  const mc = norm360(deg(Math.atan2(Math.sin(ramc), Math.cos(ramc) * Math.cos(eps))))

  let asc = norm360(
    deg(
      Math.atan2(
        Math.cos(ramc),
        -(Math.sin(eps) * Math.tan(phi) + Math.cos(eps) * Math.sin(ramc)),
      ),
    ),
  )
  // Асцендент всегда идёт за MC по ходу знаков; иначе берём противоположную точку.
  if (norm360(asc - mc) > 180) asc = norm360(asc + 180)

  return { asc, mc }
}

// ── Карта ────────────────────────────────────────────────────────────

export const PLANETS = [
  { id: 'sun',     glyph: '☉', ru: 'Солнце',   az: 'Günəş',   en: 'Sun',     tr: 'Güneş' },
  { id: 'moon',    glyph: '☾', ru: 'Луна',     az: 'Ay',      en: 'Moon',    tr: 'Ay' },
  { id: 'mercury', glyph: '☿', ru: 'Меркурий', az: 'Merkuri', en: 'Mercury', tr: 'Merkür' },
  { id: 'venus',   glyph: '♀', ru: 'Венера',   az: 'Venera',  en: 'Venus',   tr: 'Venüs' },
  { id: 'mars',    glyph: '♂', ru: 'Марс',     az: 'Mars',    en: 'Mars',    tr: 'Mars' },
  { id: 'jupiter', glyph: '♃', ru: 'Юпитер',   az: 'Yupiter', en: 'Jupiter', tr: 'Jüpiter' },
  { id: 'saturn',  glyph: '♄', ru: 'Сатурн',   az: 'Saturn',  en: 'Saturn',  tr: 'Satürn' },
  { id: 'uranus',  glyph: '♅', ru: 'Уран',     az: 'Uran',    en: 'Uranus',  tr: 'Uranüs' },
  { id: 'neptune', glyph: '♆', ru: 'Нептун',   az: 'Neptun',  en: 'Neptune', tr: 'Neptün' },
] as const

export type PlanetId = (typeof PLANETS)[number]['id']

export function planetName(id: string, lang: Lang): string {
  const p = PLANETS.find((x) => x.id === id)
  return p ? loc(p, lang) : id
}

/** Долгота планеты — Солнце и Луна считаются своими рядами. */
function longitudeOf(id: string, jd: number): number {
  if (id === 'sun') return sunLongitude(jd)
  if (id === 'moon') return moonLongitude(jd)
  return planetLongitude(id, jd)
}

export interface Placement {
  planet: PlanetId
  /** Эклиптическая долгота, градусы 0–360. */
  longitude: number
  /** Индекс знака 0–11 (0 — Овен). */
  sign: number
  /** Градус внутри знака, 0–30. */
  degreeInSign: number
  /** Дом цельнознаковой системы, 1–12; null, если время рождения неизвестно. */
  house: number | null
  retrograde: boolean
  /** Ближе 1° к границе знака — точности эфемерид может не хватить. */
  nearCusp: boolean
}

export interface NatalChart {
  placements: Placement[]
  /** null, если время рождения не указано. */
  angles: (Angles & { ascSign: number; mcSign: number }) | null
  /** Момент, на который посчитана карта. */
  utc: string
  /** Время рождения неизвестно — карта построена на полдень по месту. */
  approximateTime: boolean
  /** Дата вне диапазона пригодности элементов JPL (1800–2050). */
  outOfRange: boolean
}

export interface BirthData {
  year: number
  month: number
  day: number
  /** Часы и минуты по местному времени; null — время неизвестно. */
  hour: number | null
  minute: number | null
  latitude: number
  longitude: number
  timeZone: string
}

export function natalChart(birth: BirthData): NatalChart {
  const approximateTime = birth.hour === null
  // Без известного времени берём местный полдень: так ошибка по Луне
  // минимальна (максимум пол-суток в любую сторону).
  const utc = localToUtc(
    birth.year,
    birth.month,
    birth.day,
    approximateTime ? 12 : birth.hour!,
    approximateTime ? 0 : (birth.minute ?? 0),
    birth.timeZone,
  )
  const jd = julianDay(utc)

  const ang = approximateTime ? null : angles(jd, birth.latitude, birth.longitude)
  const ascSign = ang ? Math.floor(ang.asc / 30) : null

  const placements: Placement[] = PLANETS.map((p) => {
    const longitude = longitudeOf(p.id, jd)
    const sign = Math.floor(longitude / 30)
    const degreeInSign = longitude - sign * 30
    // Ретроградность — по знаку суточного изменения долготы.
    const next = longitudeOf(p.id, jd + 1)
    const delta = ((next - longitude + 540) % 360) - 180
    return {
      planet: p.id,
      longitude,
      sign,
      degreeInSign,
      house: ascSign === null ? null : ((sign - ascSign + 12) % 12) + 1,
      retrograde: delta < 0,
      nearCusp: degreeInSign < 1 || degreeInSign > 29,
    }
  })

  return {
    placements,
    angles: ang
      ? { ...ang, ascSign: Math.floor(ang.asc / 30), mcSign: Math.floor(ang.mc / 30) }
      : null,
    utc: utc.toISOString(),
    approximateTime,
    outOfRange: birth.year < 1800 || birth.year > 2050,
  }
}

/** Знак Солнца по дате — то же вычисление, что и в карте, без места и времени. */
export function sunSignOf(year: number, month: number, day: number): number {
  const jd = julianDay(new Date(Date.UTC(year, month - 1, day, 12)))
  return Math.floor(sunLongitude(jd) / 30)
}

/**
 * Положения светил на заданный день — небо «сегодня», без привязки к человеку.
 *
 * Берём полдень UTC: за сутки быстрее всех идёт Луна (около 13°), и полдень
 * даёт наименьшую ошибку на любой часовой пояс. Домов и Асцендента здесь нет —
 * для них нужны время и место рождения, а это небо общее для всех.
 */
export function skyOn(year: number, month: number, day: number): Placement[] {
  const jd = julianDay(new Date(Date.UTC(year, month - 1, day, 12, 0, 0)))
  return PLANETS.map((p) => {
    const longitude = longitudeOf(p.id, jd)
    const sign = Math.floor(longitude / 30)
    const next = longitudeOf(p.id, jd + 1)
    const delta = ((next - longitude + 540) % 360) - 180
    return {
      planet: p.id,
      longitude,
      sign,
      degreeInSign: longitude - sign * 30,
      house: null,
      retrograde: delta < 0,
      nearCusp: false,
    }
  })
}
